import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Shield, CheckCircle, XCircle, Clock, Eye, FileText, 
  Loader2, ChevronLeft, Search, Download 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  verified: { label: 'Vérifié', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-800', icon: XCircle },
  unverified: { label: 'Non vérifié', color: 'bg-gray-100 text-gray-800', icon: Shield },
};

export default function AdminVerifications() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, verified: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const authHeader = { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` };

  const fetchVerifications = async (status = statusFilter) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/verifications?status=${status}`,
        { headers: authHeader }
      );
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setCounts(data.counts || {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVerifications(); }, [statusFilter]);

  const handleApprove = async (providerId) => {
    setActionLoading(providerId);
    try {
      const res = await fetch(`${API_URL}/api/admin/verifications/${providerId}/approve`, {
        method: 'PUT', headers: authHeader
      });
      if (res.ok) {
        toast.success('Prestataire approuvé !');
        fetchVerifications();
      } else {
        toast.error('Erreur lors de l\'approbation');
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!selectedProvider) return;
    setActionLoading(selectedProvider.provider_id);
    try {
      const res = await fetch(`${API_URL}/api/admin/verifications/${selectedProvider.provider_id}/reject`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason })
      });
      if (res.ok) {
        toast.success('Prestataire rejeté');
        setRejectDialogOpen(false);
        setRejectReason('');
        setSelectedProvider(null);
        fetchVerifications();
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setActionLoading(null); }
  };

  const getDocumentUrl = (path) => {
    const tok = token || localStorage.getItem('token');
    return `${API_URL}/api/files/${path}?auth=${tok}`;
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.unverified;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-verifications">
      <div className="container-app max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Vérification des prestataires</h1>
            <p className="text-muted-foreground text-sm">Validez les documents et activez les comptes prestataires</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className={`cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setStatusFilter('pending')}>
            <CardContent className="pt-5 pb-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{counts.pending || 0}</p>
              <p className="text-xs text-muted-foreground">En attente</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${statusFilter === 'verified' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setStatusFilter('verified')}>
            <CardContent className="pt-5 pb-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{counts.verified || 0}</p>
              <p className="text-xs text-muted-foreground">Vérifiés</p>
            </CardContent>
          </Card>
          <Card className={`cursor-pointer transition-all ${statusFilter === 'rejected' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setStatusFilter('rejected')}>
            <CardContent className="pt-5 pb-4 text-center">
              <XCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{counts.rejected || 0}</p>
              <p className="text-xs text-muted-foreground">Rejetés</p>
            </CardContent>
          </Card>
        </div>

        {/* Provider List */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto" />
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucun prestataire dans cette catégorie</p>
          </div>
        ) : (
          <div className="space-y-4">
            {providers.map(provider => (
              <Card key={provider.provider_id} className="overflow-hidden" data-testid={`verification-${provider.provider_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-14 h-14 flex-shrink-0">
                      {provider.profile_image ? <AvatarImage src={provider.profile_image} /> : null}
                      <AvatarFallback className="text-lg bg-accent text-accent-foreground">{provider.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{provider.name}</h3>
                        <StatusBadge status={provider.verification_status} />
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{provider.email}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>Catégorie: <strong>{provider.service_category}</strong></span>
                        <span>|</span>
                        <span>Ville: <strong>{provider.city || provider.location || 'N/A'}</strong></span>
                        <span>|</span>
                        <span>Tarif: <strong>{provider.hourly_rate}€/h</strong></span>
                        {provider.phone && <><span>|</span><span>Tél: {provider.phone}</span></>}
                      </div>
                      {provider.bio && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{provider.bio}</p>}
                      
                      {/* Document */}
                      {provider.verification_document_path ? (
                        <div className="mt-3 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-accent" />
                          <a 
                            href={getDocumentUrl(provider.verification_document_path)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-accent hover:underline"
                            data-testid={`doc-link-${provider.provider_id}`}
                          >
                            {provider.verification_document_name || 'Document de vérification'}
                          </a>
                          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-muted-foreground italic">Aucun document fourni</p>
                      )}

                      {provider.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded-lg text-xs text-red-700">
                          Motif du rejet : {provider.rejection_reason}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {provider.verification_status !== 'verified' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleApprove(provider.provider_id)}
                          disabled={actionLoading === provider.provider_id}
                          data-testid={`approve-btn-${provider.provider_id}`}
                        >
                          {actionLoading === provider.provider_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" />Approuver</>}
                        </Button>
                      )}
                      {provider.verification_status !== 'rejected' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedProvider(provider); setRejectDialogOpen(true); }}
                          data-testid={`reject-btn-${provider.provider_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />Rejeter
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rejeter {selectedProvider?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Textarea
                placeholder="Motif du rejet (optionnel)..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="reject-reason-input"
              />
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setRejectDialogOpen(false)}>Annuler</Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white" 
                  onClick={handleReject}
                  disabled={actionLoading === selectedProvider?.provider_id}
                  data-testid="confirm-reject-btn"
                >
                  Confirmer le rejet
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
